/**
 * 安全埋点工具 - 不影响任何现有业务逻辑
 *
 * 设计原则：
 * 1. fire-and-forget：永远不阻塞业务代码
 * 2. 静默失败：埋点异常只 console.error，不抛出
 * 3. 无 UI 影响：不触发 loading、toast 等
 * 4. 兼容三种渠道：iOS APP / Android APP / H5 浏览器
 */

import store from '@/store'
import Bridge from '@/utils/bridge'
import { isiOS, isAndroid, getCookie } from '@/utils/index'

/**
 * 获取当前埋点渠道
 * 注意：isAndroid() 检测 UA 含 'Android'，安卓浏览器也会返回 true，
 * 必须结合 window.WebViewJavascriptBridge 才能确认是 Android APP
 *
 * @returns {string} 'ios_app' | 'android_app' | 'h5_browser'
 */
export function getChannel() {
  try {
    if (isiOS()) {
      return 'ios_app'
    }
    if (isAndroid() && window.WebViewJavascriptBridge) {
      return 'android_app'
    }
    return 'h5_browser'
  } catch (err) {
    return 'h5_browser'
  }
}

/**
 * 是否为 APP 环境（iOS APP 或 Android APP）
 */
function isApp() {
  return getChannel() !== 'h5_browser'
}

/**
 * API埋点：通过 /tracking/event 接口上报
 * 安全包装：catch 所有异常，不影响调用方
 *
 * 保护机制：无 token 时跳过 API 上报，避免触发 request.js 响应拦截器的 0001 路由跳转
 *
 * @param {Object} pageInfo - 埋点信息
 * @param {string} pageInfo.eventCode - 事件编码，如 'page_view', 'click', 'page_leave'
 * @param {string} pageInfo.pageName - 页面名称，如 'smsLogin'
 * @param {string} pageInfo.operationName - 操作名称，如 'smsLogin-next'
 * @param {string} [pageInfo.operationStatus] - 操作状态，如 '1'
 * @param {number} [pageInfo.eventDuration] - 事件持续时间(毫秒)
 */
export function trackEvent(pageInfo) {
  try {
    if (!pageInfo || typeof pageInfo !== 'object') {
      console.warn('[trackEvent] 参数无效，跳过:', pageInfo)
      return
    }
    // 注入渠道标识，区分 ios_app / android_app / h5_browser
    pageInfo.channel = getChannel()
    // 无 token 时跳过 API 上报，避免 0001 响应触发路由跳转死循环
    var token = getCookie('token')
    if (!token) {
      console.warn('[trackEvent] 无token，跳过API埋点:', pageInfo.pageName, pageInfo.operationName)
      return
    }
    store.dispatch('eventPoint', pageInfo).then(function () {
      // 上报成功，静默处理
    }).catch(function (err) {
      console.error('[trackEvent] 上报失败:', err)
    })
  } catch (err) {
    console.error('[trackEvent] 异常:', err)
  }
}

/**
 * 原生埋点：通过 JSBridge 上报页面浏览/离开
 * 仅在 APP 环境生效，H5 环境自动跳过
 *
 * @param {string} type - 'pointPageView' 或 'pointPageLeave'
 * @param {Object} pageInfo - 埋点信息
 * @param {string} pageInfo.pageName - 页面名称
 * @param {string} [pageInfo.operationName] - 操作名称
 */
export function trackNative(type, pageInfo) {
  try {
    if (isApp()) {
      Bridge(type, pageInfo)
    }
  } catch (err) {
    console.error('[trackNative] 异常:', err)
  }
}

/**
 * 页面浏览埋点（组合：原生 + API）
 * APP环境：仅走原生埋点（与现有 repayment/detail.vue 模式一致）
 * H5环境：仅走API埋点
 *
 * @param {string} pageName - 页面名称，如 'smsLogin'
 * @param {string} [operationName] - 操作名称，默认 `${pageName}-pageview`
 */
export function trackPageView(pageName, operationName) {
  var opName = operationName || (pageName + '-pageview')
  var info = {
    eventCode: 'page_view',
    pageName: pageName,
    operationName: opName
  }
  if (isApp()) {
    // APP环境：仅原生埋点（与现有代码模式一致，避免重复上报）
    trackNative('pointPageView', info)
  } else {
    // H5浏览器环境：仅API埋点
    trackEvent(info)
  }
}

/**
 * 页面离开埋点（组合：原生 + API）
 * APP环境：仅走原生埋点
 * H5环境：仅走API埋点
 *
 * @param {string} pageName - 页面名称
 * @param {string} [operationName] - 操作名称，默认 `${pageName}-pageleave`
 * @param {number} [startTime] - 页面进入时间戳，用于计算停留时长
 */
export function trackPageLeave(pageName, operationName, startTime) {
  var opName = operationName || (pageName + '-pageleave')
  var info = {
    eventCode: 'page_leave',
    pageName: pageName,
    operationName: opName
  }
  if (startTime) {
    info.eventDuration = Date.now() - startTime
  }
  if (isApp()) {
    // APP环境：仅原生埋点
    trackNative('pointPageLeave', info)
  } else {
    // H5浏览器环境：仅API埋点
    trackEvent(info)
  }
}

/**
 * 点击事件埋点（仅API，非阻塞）
 *
 * @param {string} pageName - 页面名称
 * @param {string} operationName - 操作名称，如 'smsLogin-next'
 * @param {string} [operationStatus='1'] - 操作状态
 * @param {number} [startTime] - 起始时间戳，用于计算时长
 */
export function trackClick(pageName, operationName, operationStatus, startTime) {
  var info = {
    eventCode: 'click',
    pageName: pageName,
    operationName: operationName,
    operationStatus: operationStatus || '1'
  }
  if (startTime) {
    info.eventDuration = Date.now() - startTime
  }
  trackEvent(info)
}

export default {
  getChannel,
  trackEvent,
  trackNative,
  trackPageView,
  trackPageLeave,
  trackClick
}



// WEBPACK FOOTER //
// ./src/utils/track.js